/**
 * Partner key constants
 */
export const PARTNER_KEYS = {
  CLALIT: "CLALIT",
  MEHUDET: "MEHUDET",
  OVDIM_BARI: "OVDIM_BARI",
  LEUMIT: "LEUMIT",
  MAOFLAAMIT: "MAOFLAAMIT",
  NONE: "NONE",
};

/**
 * The query-string parameter each partner opens the web app with. The token is
 * whatever follows the parameter name.
 */
const MEHUDET_USER_DATA_PARAM = "Meuhedetuserdata";
const CLALIT_USER_DATA_PARAM = "userdata";
const OVDIM_BARI_USER_DATA_PARAM = "Ovdimbariuserdata";
const LEUMIT_USER_DATA_PARAM = "Leumituserdata";
const MAOFLAAMIT_USER_DATA_PARAM = "Maoflaamituserdata";

/**
 * Reads the partner and the encrypted token out of a query string.
 *
 * Pure — no dispatch, no storage — so a component can call it during render to
 * learn the partner before the handshake has dispatched setPartner.
 *
 * The order of the checks matters: "userdata" is a substring of all four of the
 * other parameter names, so Clalit has to be tested last.
 *
 * @param {string} search - e.g. "?userdata=ABC123"
 * @returns {{partner: string, userData: string}} userData is "" when no known
 *   parameter is present.
 */
export function parsePartnerParams(search) {
  const [, queryString] = (search ?? "").split("?");
  if (!queryString) return { partner: PARTNER_KEYS.NONE, userData: "" };

  const match = (param, partner) =>
    queryString.includes(param) ? { partner, userData: queryString.split(param)[1] } : null;

  const found =
    match(MEHUDET_USER_DATA_PARAM, PARTNER_KEYS.MEHUDET) ??
    match(LEUMIT_USER_DATA_PARAM, PARTNER_KEYS.LEUMIT) ??
    match(OVDIM_BARI_USER_DATA_PARAM, PARTNER_KEYS.OVDIM_BARI) ??
    match(MAOFLAAMIT_USER_DATA_PARAM, PARTNER_KEYS.MAOFLAAMIT) ??
    match(CLALIT_USER_DATA_PARAM, PARTNER_KEYS.CLALIT);

  if (!found) return { partner: PARTNER_KEYS.NONE, userData: "" };
  // slice(1) drops the "=" between the parameter name and the token.
  return { partner: found.partner, userData: found.userData ? found.userData.slice(1) : "" };
}

/**
 * The best partner known during *this render*.
 *
 * LayoutV2 dispatches setPartner from inside silentLogin, so the store still reads
 * NONE on the first paint — and on a first-ever visit localStorage.partnerKey has
 * not been written yet either. Anything that has to branch on the partner before
 * the handshake resolves (the "/" dispatcher) needs this instead of the selector.
 *
 * @param {string} storePartner - the value from selectPartner
 * @returns {string} a PARTNER_KEYS value
 */
export function resolvePartnerSync(storePartner) {
  if (storePartner && storePartner !== PARTNER_KEYS.NONE) return storePartner;

  // The live URL first, then the copy silentLogin caches for pages that carry no
  // query string of their own.
  const search = document.location.search || localStorage.getItem("queryParams") || "";
  const { partner } = parsePartnerParams(search);
  if (partner !== PARTNER_KEYS.NONE) return partner;

  return localStorage.getItem("partnerKey") || PARTNER_KEYS.NONE;
}

/**
 * Checks if a partner key exists in localStorage
 * @param {string} partnerKey - The partner key to validate
 * @returns {boolean} Returns true if the partner key exists in localStorage
 * @throws {Error} If the partner key is invalid
 */
export function checkPartnerKeyExists(partnerKey) {
  if (!partnerKey || typeof partnerKey !== "string") {
    throw new Error("Valid partner key is required");
  }

  try {
    const storedPartnerKey = localStorage.getItem("partnerKey");

    return storedPartnerKey === partnerKey;
  } catch (error) {
    throw new Error(`Error checking partner key in localStorage: ${error.message}`);
  }
}

/**
 * Returns the ExternalSystem ID for the current partner
 * @returns {number} 1 for Clalit, 2 for Mehudet, 3 for Leumit, 4 for OvdimBari
 */
export function getExternalSystem() {
  if (checkPartnerKeyExists(PARTNER_KEYS.CLALIT)) return 1;
  if (checkPartnerKeyExists(PARTNER_KEYS.MEHUDET)) return 2;
  if (checkPartnerKeyExists(PARTNER_KEYS.LEUMIT)) return 3;
  if (checkPartnerKeyExists(PARTNER_KEYS.OVDIM_BARI)) return 4;
  if (checkPartnerKeyExists(PARTNER_KEYS.MAOFLAAMIT)) return 5;
  return 1;
}

/**
 * Returns the request payload fragment identifying the system.
 * - OvdimBari → { MultiExternalSystem: 1 }
 * - Otherwise → { ExternalSystem: getExternalSystem() }
 *
 * Use this for endpoints that already sent ExternalSystem and now must
 * send MultiExternalSystem instead for OvdimBari users.
 * @returns {Object}
 */
export function getExternalSystemParams() {
  if (checkPartnerKeyExists(PARTNER_KEYS.OVDIM_BARI)) {
    return { MultiExternalSystem: 1 };
  }
  if (checkPartnerKeyExists(PARTNER_KEYS.MAOFLAAMIT)) {
    return { SecondMultiExternalSystem: 1 };
  }
  return { ExternalSystem: getExternalSystem() };
}

/**
 * Returns { MultiExternalSystem: 1 } for OvdimBari, {} otherwise.
 *
 * Use this for endpoints that previously sent NO system identifier and
 * should only start sending one for OvdimBari (preserving the existing
 * behavior of other partners).
 * @returns {Object}
 */
export function getOvdimBariMultiExternalSystemParams() {
  if (checkPartnerKeyExists(PARTNER_KEYS.OVDIM_BARI)) {
    return { MultiExternalSystem: 1 };
  }
  return {};
}

/**
 * Returns { SecondMultiExternalSystem: 1 } for Maoflaamit, {} otherwise.
 *
 * Use this for endpoints that previously sent NO system identifier and
 * should only start sending one for Maoflaamit (preserving the existing
 * behavior of other partners).
 * @returns {Object}
 */
export function getMaoflaamitSecondMultiExternalSystemParams() {
  if (checkPartnerKeyExists(PARTNER_KEYS.MAOFLAAMIT)) {
    return { SecondMultiExternalSystem: 1 };
  }
  return {};
}

/**
 * Saves a partner key to localStorage
 * @param {string} partnerKey - The partner key to save
 * @throws {Error} If the partner key is invalid
 */
export function savePartnerKey(partnerKey) {
  if (!partnerKey || typeof partnerKey !== "string") {
    throw new Error("Valid partner key is required");
  }

  try {
    localStorage.setItem("partnerKey", partnerKey);
  } catch (error) {
    throw new Error(`Error saving partner key to localStorage: ${error.message}`);
  }
}

/**
 * Saves the isOrthodox flag to localStorage
 * @param {boolean} isOrthodox - The boolean value to save
 * @throws {Error} If the value is not a boolean
 */
export function saveIsOrthodox(isOrthodox) {
  if (typeof isOrthodox !== "boolean") {
    throw new Error("isOrthodox must be a boolean value");
  }

  try {
    localStorage.setItem("isOrthodox", JSON.stringify(isOrthodox));
  } catch (error) {
    throw new Error(`Error saving isOrthodox to localStorage: ${error.message}`);
  }
}

/**
 * Gets the isOrthodox flag from localStorage
 * @returns {boolean} The stored isOrthodox value, defaults to false if not set
 */
export function getIsOrthodox() {
  try {
    const value = localStorage.getItem("isOrthodox");
    return value ? JSON.parse(value) : false;
  } catch (error) {
    console.error("Error reading isOrthodox from localStorage:", error);
    return false;
  }
}

/**
 * Saves the ShowOldClalit flag to localStorage.
 *
 * Server-driven (the Getuserdata response), but read during render by isV2Enabled, which runs
 * before the handshake lands. Persisting it here — the saveIsOrthodox precedent — is what lets
 * getShowOldClalit answer synchronously on the first painted frame of the *next* visit.
 * @param {boolean} showOldClalit
 * @throws {Error} If the value is not a boolean
 */
export function saveShowOldClalit(showOldClalit) {
  if (typeof showOldClalit !== "boolean") {
    throw new Error("showOldClalit must be a boolean value");
  }

  try {
    localStorage.setItem("showOldClalit", JSON.stringify(showOldClalit));
  } catch (error) {
    throw new Error(`Error saving showOldClalit to localStorage: ${error.message}`);
  }
}

/**
 * Gets the ShowOldClalit flag from localStorage.
 *
 * Defaults to FALSE when nothing is stored, i.e. "show V2". That direction matters: on a
 * first-ever visit storage is empty and the flag has not arrived yet, and isV2Enabled itself
 * falls back to false, so defaulting to "old flow" would paint one legacy frame to every
 * first-time Clalit user before flipping to V2 — the flash the resolvePartnerSync rule exists to
 * prevent. It also means a server that never sets the appSetting keeps today's behaviour.
 * @returns {boolean} The stored showOldClalit value, defaults to false if not set
 */
export function getShowOldClalit() {
  try {
    const value = localStorage.getItem("showOldClalit");
    return value ? JSON.parse(value) : false;
  } catch (error) {
    console.error("Error reading showOldClalit from localStorage:", error);
    return false;
  }
}

/**
 * Saves the isSubsidyOver flag to localStorage
 * @param {boolean} isSubsidyOver - The boolean value to save
 * @throws {Error} If the value is not a boolean
 */
export function saveIsSubsidyOver(isSubsidyOver) {
  if (typeof isSubsidyOver !== "boolean") {
    throw new Error("isSubsidyOver must be a boolean value");
  }

  try {
    localStorage.setItem("isSubsidyOver", JSON.stringify(isSubsidyOver));
  } catch (error) {
    throw new Error(`Error saving isSubsidyOver to localStorage: ${error.message}`);
  }
}

/**
 * Gets the isSubsidyOver flag from localStorage
 * @returns {boolean} The stored isSubsidyOver value, defaults to false if not set
 */
export function getIsSubsidyOver() {
  try {
    const value = localStorage.getItem("isSubsidyOver");
    return value ? JSON.parse(value) : false;
  } catch (error) {
    console.error("Error reading isSubsidyOver from localStorage:", error);
    return false;
  }
}

/**
 * Remove the isSubsidyOver flag from localStorage
 * @returns {boolean} The stored isSubsidyOver value, defaults to false if not set
 */
export function removeIsSubsidyOver() {
  localStorage.removeItem("isSubsidyOver");
}

/**
 * Saves the isMinor flag to localStorage
 * @param {boolean} isMinor - The boolean value to save
 * @throws {Error} If the value is not a boolean
 */
export function saveIsMinor(isMinor) {
  if (typeof isMinor !== "boolean") {
    throw new Error("isMinor must be a boolean value");
  }

  try {
    localStorage.setItem("isMinor", JSON.stringify(isMinor));
  } catch (error) {
    throw new Error(`Error saving isMinor to localStorage: ${error.message}`);
  }
}

/**
 * Gets the isMinor flag from localStorage
 * @returns {boolean} The stored isMinor value, defaults to false if not set
 */
export function getIsMinor() {
  try {
    const value = localStorage.getItem("isMinor");
    return value ? JSON.parse(value) : false;
  } catch (error) {
    console.error("Error reading isMinor from localStorage:", error);
    return false;
  }
}
